/**
 * `<VerifyEmailRoute>` — `/verify-email?email=<addr>`
 *
 * Per `aidlc-docs/construction/auth-redesign/functional-design/frontend-components.md § 4`.
 *
 * Lands the user after `/signup` (email+password registration). WorkOS
 * has emailed a 6-digit one-time code; this page collects the code and
 * posts it to `POST /api/auth/verify-email`. On success **no session is
 * issued** — the user is redirected back to `/login` with a success
 * toast so they re-authenticate with their freshly-verified credentials
 * (per FD D7).
 *
 * Secondary action: "Resend code" calls `POST /api/auth/resend-verification`,
 * gated by a 30-second client-side cooldown to limit hammering. The
 * server also enforces a 3-per-10min rate limit — surfaced via toast.
 *
 * If `?email=` is missing, we redirect to `/signup` (the page is not
 * useful without an address — and pre-filling defends against tab-loss).
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import { verifyEmailSchema, type VerifyEmailDto } from "@cometchat-ai/auth/shared";
import {
  Alert,
  AlertDescription,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "../components/icons";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "../layouts/auth-layout";
import { formatApiError } from "../lib/format-error";

const RESEND_COOLDOWN_SECONDS = 30;

export function VerifyEmailRoute() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";

  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!email) navigate("/signup", { replace: true });
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const form = useForm<VerifyEmailDto>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: { email, code: "" },
  });

  useEffect(() => {
    form.setValue("email", email);
  }, [email, form]);

  const verify = useMutation({
    mutationFn: (values: VerifyEmailDto) => authClient.verifyEmail(values),
    onSuccess: () => {
      toast.success(t("verifyEmail.verifySuccessTitle"), {
        description: t("verifyEmail.verifySuccessDescription"),
      });
      navigate("/login", { replace: true });
    },
    onError: (err: AuthClientError) => {
      toast.error(t("verifyEmail.verifyFailed"), { description: formatApiError(err) });
    },
  });

  const resend = useMutation({
    mutationFn: () => authClient.resendVerification({ email }),
    onSuccess: () => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success(t("verifyEmail.resentTitle"), {
        description: t("verifyEmail.resentDescription"),
      });
    },
    onError: (err: AuthClientError) => {
      toast.error(t("verifyEmail.resendFailed"), { description: formatApiError(err) });
    },
  });

  const canResend = Boolean(email) && cooldown === 0 && !resend.isPending;

  return (
    <AuthLayout
      title={t("verifyEmail.title")}
      subtitle={
        email ? t("verifyEmail.description", { email }) : t("verifyEmail.descriptionNoEmail")
      }
    >
      <div className="space-y-4" data-testid="verify-email-body">
        {email ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((values) => verify.mutate(values))}
              className="space-y-4"
              data-testid="verify-email-form"
              noValidate
            >
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("verifyEmail.codeLabel")}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder={t("verifyEmail.codePlaceholder")}
                        data-testid="verify-email-code"
                        className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="h-10 w-full rounded-xl hover:bg-primary-hover"
                disabled={verify.isPending}
                data-testid="verify-email-submit"
              >
                {verify.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                )}
                {verify.isPending ? t("verifyEmail.verifying") : t("verifyEmail.verifySubmit")}
              </Button>
            </form>
          </Form>
        ) : null}

        {email ? <Separator /> : null}

        <Alert>
          <AlertDescription>{t("verifyEmail.didntReceive")}</AlertDescription>
        </Alert>

        {email ? (
          <Button
            type="button"
            variant="outline"
            className="h-12 w-full rounded-xl"
            disabled={!canResend}
            onClick={() => resend.mutate()}
            data-testid="verify-email-resend"
          >
            {resend.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {cooldown > 0
              ? t("verifyEmail.sendAnotherIn", { seconds: cooldown })
              : t("verifyEmail.sendAnother")}
          </Button>
        ) : null}

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/signup" className="text-primary underline-offset-4 hover:underline">
            {t("verifyEmail.backToSignup")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
